// EComVendorProductsTable.tsx
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { currencyFormat, PF } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { getVendorProductsPage, VendorProducts } from 'api/products/_requests';
import AdvancetableFootervendorProduct from 'components/base/AdvancetableFootervendorProduct';

const EComVendorProductsTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [productsData, setProductsData] = useState<VendorProducts[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [total, setTotal] = useState(0);

  const handleEdit = (productId: string) => {
    navigate(`/edit-product/${productId}`);
  };

  const fetchProductsData = async () => {
    const response = await getVendorProductsPage(page, limit);
    const temp = response.data.map((item: VendorProducts) => ({
      ...item,
      thumbnail: PF + (item.thumbnail ?? 'goods')
    }));
    setProductsData(temp);
    setTotal(response.total);
    setPage(response.page);
  };

  const columns: ColumnDef<VendorProducts>[] = [
    {
      id: 'productImage',
      accessorKey: '',
      cell: ({ row: { original } }) => {
        const { thumbnail } = original;
        return (
          <div className="rounded-2 border border-translucent d-inline-block">
            <img
              src={thumbnail}
              alt="product"
              width={53}
              className="image-square"
            />
          </div>
        );
      },
      meta: {
        cellProps: { className: 'py-0' },
        headerProps: { style: { width: '7%' } }
      }
    },
    {
      accessorKey: 'product',
      header: 'Products',
      cell: ({ row: { original } }) => (
        <Link
          to={`/product-details/${original.slug}`}
          className="fw-semibold line-clamp-1"
        >
          {original.name}
        </Link>
      ),
      meta: {
        headerProps: { style: { minWidth: 250, width: '30%' } },
        cellProps: { className: 'pe-11' }
      }
    },
    {
      accessorKey: 'price',
      header: () => 'Price',
      cell: ({ row: { original } }) =>
        currencyFormat(original.price, { currency: 'SAR', style: 'currency' }),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-end' },
        cellProps: { className: 'text-center fw-semibold' }
      }
    },
    {
      accessorKey: 'action',
      header: () => 'Action',
      cell: ({ row: { original } }) => (
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleEdit(original._id)}
        >
          <FontAwesomeIcon icon={faEdit} />
        </Button>
      ),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-end' },
        cellProps: { className: 'text-end fw-semibold' }
      }
    }
  ];

  useEffect(() => {
    fetchProductsData();
  }, [page, limit]);

  const table = useAdvanceTable({
    data: productsData,
    columns,
    pageSize: 15,
    pagination: true,
    sortable: true
  });

  useEffect(() => {
    table.setPageSize(limit);
  }, [limit]);

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="border-y border-translucent">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvancetableFootervendorProduct
            pagination
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            total={total}
          />
        </div>
      </AdvanceTableProvider>
    </div>
  );
};

export default EComVendorProductsTable;
